import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { Lock, PlayCircle, Loader2, ArrowRight, Clock, Layers, CheckCircle2 } from "lucide-react";
import AskIvana from "@/components/AskIvana";
import SkoolInfoModal from "@/components/SkoolInfoModal";

const Members = () => {
  const { user, loading: authLoading } = useAuth();
  const [modules, setModules] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSkoolModalOpen, setIsSkoolModalOpen] = useState(false);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await axios.get(`${API}/members/modules`, {
          withCredentials: true,
        });
        setModules(response.data.modules);
        setIsAuthenticated(response.data.authenticated);
      } catch (error) {
        console.error("Failed to load modules", error);
      } finally {
        setLoading(false);
      }
    };
    if (!authLoading) fetchModules();
  }, [user, authLoading]);

  return (
    <div
      className="min-h-screen bg-[#F9F8F6] pt-32 pb-20 px-6 md:px-12"
      data-testid="members-page"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <span className="overline mb-4 block">
            <Layers className="inline-block w-3 h-3 mr-2" />
            Member Library
          </span>
          <h1 className="font-heading text-4xl md:text-6xl text-[#2C3524] leading-[1.1] mb-6">
            The <span className="italic text-[#C5A059]">Perimenopause Decoded</span> Library
          </h1>
          <p className="text-[#4A5543] text-lg md:text-xl font-light leading-relaxed">
            Six modules. Every framework I use with my private clients.
            {!isAuthenticated && (
              <span className="block mt-3 text-[#2C3524]">
                Module 1 is free to preview.{" "}
                <button
                  onClick={() => setIsSkoolModalOpen(true)}
                  className="text-[#C5A059] underline hover:no-underline font-medium"
                  data-testid="members-unlock-cta"
                >
                  Join Perimenopause Decoded
                </button>{" "}
                to unlock the rest.
              </span>
            )}
          </p>
        </div>

        {loading ? (
          <div className="py-20 text-center">
            <Loader2 className="w-10 h-10 text-[#C5A059] animate-spin mx-auto" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {modules.map((module) => (
              <div
                key={module.id}
                data-testid={`module-${module.id}`}
                className={`relative rounded-3xl p-8 md:p-10 transition-all duration-500 border ${
                  module.unlocked
                    ? "bg-white border-[#E3DAC9] hover:border-[#C5A059] hover:-translate-y-1"
                    : "bg-[#F2F4F0] border-[#E3DAC9]/50"
                }`}
              >
                {/* Lock/Play icon */}
                <div className="flex items-start justify-between mb-6">
                  <span className="font-heading text-5xl text-[#C5A059]/30 italic">
                    {module.number}
                  </span>
                  {module.unlocked ? (
                    <div className="w-11 h-11 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
                      <PlayCircle
                        className="w-6 h-6 text-[#C5A059]"
                        strokeWidth={1.5}
                      />
                    </div>
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-[#4A5543]/10 flex items-center justify-center">
                      <Lock className="w-5 h-5 text-[#4A5543]" strokeWidth={1.5} />
                    </div>
                  )}
                </div>

                <h3 className="font-heading text-2xl md:text-3xl text-[#2C3524] leading-tight mb-1">
                  {module.title}
                </h3>
                <p className="text-[#C5A059] text-sm italic mb-4">
                  {module.subtitle}
                </p>

                {/* Meta */}
                <div className="flex items-center gap-4 text-xs text-[#4A5543]/70 tracking-wider uppercase mb-4">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {module.duration}
                  </span>
                  <span>•</span>
                  <span>{module.lessons_total} lessons</span>
                </div>

                <p className="text-[#4A5543] font-light leading-relaxed mb-6">
                  {module.description}
                </p>

                {/* Progress bar (only for unlocked + authenticated) */}
                {module.unlocked && isAuthenticated && (
                  <div className="mb-6">
                    <div className="flex justify-between text-xs text-[#4A5543]/70 mb-2">
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-[#C5A059]" />
                        {module.lessons_completed} / {module.lessons_total} completed
                      </span>
                      <span className="tracking-wider">{module.progress_pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-[#E3DAC9] overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#C5A059] to-[#DCC286] transition-all duration-500"
                        style={{ width: `${module.progress_pct}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Topics */}
                <ul className="mb-6 space-y-2">
                  {module.topics.map((topic, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-3 text-sm text-[#4A5543]"
                    >
                      <span className="w-1 h-1 rounded-full bg-[#C5A059]" />
                      {topic}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                {module.unlocked ? (
                  <Link
                    to={`/members/${module.id}`}
                    className="inline-flex items-center gap-2 text-[#C5A059] font-medium text-sm hover:gap-3 transition-all"
                    data-testid={`open-${module.id}`}
                  >
                    {module.lessons_completed > 0 ? "Continue Module" : "Start Module"}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <button
                    onClick={() => setIsSkoolModalOpen(true)}
                    className="inline-flex items-center gap-2 text-[#4A5543] font-medium text-sm hover:text-[#C5A059] transition-colors"
                    data-testid={`unlock-${module.id}`}
                  >
                    <Lock className="w-3 h-3" />
                    Join to unlock
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Bottom upsell — always visible so both guests and members see the path forward */}
        <div className="mt-20 rounded-3xl bg-[#2C3524] p-10 md:p-14 text-center">
          <span className="overline text-[#DCC286] mb-4 block">
            Take it deeper
          </span>
          <h2 className="font-heading text-3xl md:text-4xl text-white leading-tight mb-4">
            Ready for live coaching + community?
          </h2>
          <p className="text-white/80 font-light mb-8 max-w-xl mx-auto">
            Join Perimenopause Decoded for weekly live calls, expert Q&As,
            and the community of women walking this path with you.
          </p>
          <button
            onClick={() => setIsSkoolModalOpen(true)}
            className="btn-primary inline-flex items-center"
            data-testid="members-upgrade-cta"
          >
            Join Perimenopause Decoded
            <ArrowRight className="inline-block ml-2 w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Ask Ivana AI Chat - always available on Library page */}
      <AskIvana />

      {/* Direct-to-membership modal for high-intent upsell */}
      <SkoolInfoModal
        isOpen={isSkoolModalOpen}
        onClose={() => setIsSkoolModalOpen(false)}
      />
    </div>
  );
};

export default Members;